<script setup lang="ts">
// The menu's group icons, in the manner of Aliyun's 费用中心: flat solid
// glyphs in two greys — a dark body and a lighter facet (a roof, a tab, a
// cube's top, a bolt's upper half) — with their details cut out of the
// body, so whatever is behind the menu item, hovered or not, shows
// through. The same file in both consoles.
//
// The greys are Arco's, which its dark theme turns round, so the icons
// follow it; a group whose page is open draws its icon in the primary
// blue (SideMenu sets --mi-main and --mi-sub).
export type MenuIconName =
  | 'dashboard'
  | 'nodes'
  | 'sites'
  | 'l4'
  | 'dns'
  | 'security'
  | 'analytics'
  | 'customers'
  | 'account'
  | 'packages'
  | 'finance'
  | 'tickets'
  | 'messages'
  | 'system'

defineProps<{ name: MenuIconName }>()

// One mask per instance: two icons on a page must not share an id.
const uid = `mi-${Math.random().toString(36).slice(2, 9)}`
</script>

<template>
  <svg class="arco-icon mi" viewBox="0 0 24 24" width="1em" height="1em" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <!-- 仪表盘: a house, its roof the lighter grey, a slot cut in its front. -->
    <template v-if="name === 'dashboard'">
      <mask :id="uid">
        <rect width="24" height="24" fill="#fff" />
        <rect x="9.3" y="15.4" width="5.4" height="1.9" rx="0.95" fill="#000" />
      </mask>
      <path class="s" d="M12 2.4 21.6 9.3H2.4z" />
      <rect class="m" x="4.2" y="10.4" width="15.6" height="11" rx="1.3" :mask="`url(#${uid})`" />
    </template>

    <!-- 节点管理: two servers, a light one under a dark one, a light and a line each. -->
    <template v-else-if="name === 'nodes'">
      <mask :id="uid">
        <rect width="24" height="24" fill="#fff" />
        <circle cx="6.9" cy="7.3" r="1.15" fill="#000" />
        <rect x="9.8" y="6.5" width="6.2" height="1.6" rx="0.8" fill="#000" />
      </mask>
      <rect class="m" x="3" y="3.3" width="18" height="8" rx="1.4" :mask="`url(#${uid})`" />
      <rect class="s" x="3" y="12.7" width="18" height="8" rx="1.4" />
      <circle class="m" cx="6.9" cy="16.7" r="1.15" />
      <rect class="m" x="9.8" y="15.9" width="6.2" height="1.6" rx="0.8" />
    </template>

    <!-- 站点管理: a browser window, its title bar light, lines cut in its page. -->
    <template v-else-if="name === 'sites'">
      <mask :id="uid">
        <rect width="24" height="24" fill="#fff" />
        <rect x="6" y="12.6" width="8.4" height="1.7" rx="0.85" fill="#000" />
        <rect x="6" y="16" width="5.2" height="1.7" rx="0.85" fill="#000" />
      </mask>
      <path class="s" d="M3.8 3.4h16.4a1.3 1.3 0 0 1 1.3 1.3v3.9h-19V4.7a1.3 1.3 0 0 1 1.3-1.3z" />
      <circle class="m" cx="5.9" cy="6" r="0.95" />
      <circle class="m" cx="8.7" cy="6" r="0.95" />
      <path class="m" d="M2.5 9.6h19v9.7a1.3 1.3 0 0 1-1.3 1.3H3.8a1.3 1.3 0 0 1-1.3-1.3z" :mask="`url(#${uid})`" />
    </template>

    <!-- 应用加速: a bolt, its upper half light. -->
    <template v-else-if="name === 'l4'">
      <path class="s" d="M15.2 1.6 3.6 14h7.1l2.9-4.6z" />
      <path class="m" d="M10.7 14 8.9 22.4 20.4 9.4h-6.8z" />
    </template>

    <!-- DNS 调度: a globe, its meridian band light, its equator cut through. -->
    <template v-else-if="name === 'dns'">
      <mask :id="uid">
        <rect width="24" height="24" fill="#fff" />
        <rect x="2" y="11.15" width="20" height="1.7" fill="#000" />
      </mask>
      <g :mask="`url(#${uid})`">
        <circle class="m" cx="12" cy="12" r="9.6" />
        <ellipse class="s" cx="12" cy="12" rx="4.1" ry="9.6" />
      </g>
    </template>

    <!-- 安全防护: a shield, its left half light, a tick cut through it. -->
    <template v-else-if="name === 'security'">
      <mask :id="uid">
        <rect width="24" height="24" fill="#fff" />
        <path d="m8.1 12 2.9 2.9 5-5.1" fill="none" stroke="#000" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" />
      </mask>
      <g :mask="`url(#${uid})`">
        <path class="s" d="M12 1.9 3.4 5.2v6c0 5.2 3.6 9.1 8.6 10.9z" />
        <path class="m" d="M12 1.9l8.6 3.3v6c0 5.2-3.6 9.1-8.6 10.9z" />
      </g>
    </template>

    <!-- 日志与分析: three bars, the first light. -->
    <template v-else-if="name === 'analytics'">
      <rect class="s" x="3" y="12.4" width="4.7" height="9" rx="1.1" />
      <rect class="m" x="9.65" y="3.4" width="4.7" height="18" rx="1.1" />
      <rect class="m" x="16.3" y="8.2" width="4.7" height="13.2" rx="1.1" />
    </template>

    <!-- 客户与套餐: two people, the one behind light. -->
    <template v-else-if="name === 'customers'">
      <circle class="s" cx="15.9" cy="7" r="3.3" />
      <path class="s" d="M11.6 20.6c.3-4.4 2.2-6.9 4.6-6.9 3 0 5.3 2.6 5.3 6.9z" />
      <circle class="m" cx="9" cy="8" r="3.9" />
      <path class="m" d="M2.4 21.3c0-4.6 2.9-7.2 6.6-7.2s6.6 2.6 6.6 7.2z" />
    </template>

    <!-- 账户设置: a person, the head light. -->
    <template v-else-if="name === 'account'">
      <circle class="s" cx="12" cy="7.4" r="4.4" />
      <path class="m" d="M3.6 21.4c0-5.3 3.7-8.3 8.4-8.3s8.4 3 8.4 8.3z" />
    </template>

    <!-- 计费管理: a box, its top light, its right face a shade off. -->
    <template v-else-if="name === 'packages'">
      <path class="s" d="M12 2.4 20.5 7.1 12 11.8 3.5 7.1z" />
      <path class="m" d="M3.5 8.3 11.4 12.7v9.2L3.5 17.5z" />
      <path class="m side" d="M20.5 8.3 12.6 12.7v9.2l7.9-4.4z" />
    </template>

    <!-- 财务管理: a slip with a yuan sign cut in it, its corner folded light. -->
    <template v-else-if="name === 'finance'">
      <mask :id="uid">
        <rect width="24" height="24" fill="#fff" />
        <path d="M8.9 8.9 12 12.4l3.1-3.5M12 12.4v5.4M9.2 13.4h5.6M9.2 15.9h5.6" fill="none" stroke="#000" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
      </mask>
      <path class="m" d="M8.6 2.6h10.1a1.4 1.4 0 0 1 1.4 1.4v16a1.4 1.4 0 0 1-1.4 1.4H5.3A1.4 1.4 0 0 1 3.9 20V7.3z" :mask="`url(#${uid})`" />
      <path class="s" d="M3.9 7.3 8.6 2.6v3.3a1.4 1.4 0 0 1-1.4 1.4z" />
    </template>

    <!-- 工单: a ticket, its stub light, lines cut in it. -->
    <template v-else-if="name === 'tickets'">
      <mask :id="uid">
        <rect width="24" height="24" fill="#fff" />
        <rect x="6" y="9.9" width="6.6" height="1.7" rx="0.85" fill="#000" />
        <rect x="6" y="13.2" width="4.3" height="1.7" rx="0.85" fill="#000" />
      </mask>
      <path class="m" d="M4 4.8h11.6v14.4H4a1.5 1.5 0 0 1-1.5-1.5V14.4a2.2 2.2 0 0 0 0-4.8V6.3A1.5 1.5 0 0 1 4 4.8z" :mask="`url(#${uid})`" />
      <path class="s" d="M16.7 4.8H20a1.5 1.5 0 0 1 1.5 1.5v3.3a2.2 2.2 0 0 0 0 4.8v3.3a1.5 1.5 0 0 1-1.5 1.5h-3.3z" />
    </template>

    <!-- 消息通知: an envelope, its flap cut in, a light dot of news at its
         corner, apart from it. -->
    <template v-else-if="name === 'messages'">
      <mask :id="uid">
        <rect width="24" height="24" fill="#fff" />
        <path d="M4.2 8.6l7.1 5.1 7.1-5.1" fill="none" stroke="#000" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        <circle cx="19" cy="5.8" r="5.1" fill="#000" />
      </mask>
      <rect class="m" x="1.9" y="5.6" width="18.8" height="14" rx="2" :mask="`url(#${uid})`" />
      <circle class="s" cx="19" cy="5.8" r="3.5" />
    </template>

    <!-- 系统管理: a gear, its hub light. -->
    <template v-else-if="name === 'system'">
      <path
        class="m"
        d="M10.40 4.26 L10.64 1.89 L13.36 1.89 L13.60 4.26 L16.34 5.40 L18.19 3.89 L20.11 5.81 L18.60 7.66 L19.74 10.40 L22.11 10.64 L22.11 13.36 L19.74 13.60 L18.60 16.34 L20.11 18.19 L18.19 20.11 L16.34 18.60 L13.60 19.74 L13.36 22.11 L10.64 22.11 L10.40 19.74 L7.66 18.60 L5.81 20.11 L3.89 18.19 L5.40 16.34 L4.26 13.60 L1.89 13.36 L1.89 10.64 L4.26 10.40 L5.40 7.66 L3.89 5.81 L5.81 3.89 L7.66 5.40 Z"
      />
      <circle class="s" cx="12" cy="12" r="3.7" />
    </template>
  </svg>
</template>

<style scoped>
/* Arco's greys unless an ancestor says otherwise (SideMenu, for the open
   group). */
.m {
  fill: var(--mi-main, rgb(var(--gray-8)));
}
.s {
  fill: var(--mi-sub, rgb(var(--gray-5)));
}
/* The box's third face: the body's grey, a step lighter. */
.side {
  opacity: 0.78;
}
</style>
